import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import CreateRoomTypeModal from "./createRoomType";
import { toast } from "react-toastify";
import {
  deleteRoomType,
  getRoomClassByResortIdResorCategoryId,
  getRoomTypeByResortId,
} from "../../../../../../../apis/property";
import EditRoomType from "./EditRoomType";
import RoomTypeDetailModal from "./roomTypeDetailModal";
import {
  PlusIcon,
  HomeIcon,
  PencilIcon,
  TrashIcon,
  SearchIcon,
  ViewGridIcon,
  ViewListIcon,
  CollectionIcon,
} from "@heroicons/react/outline";

const RoomType = () => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [roomType, setRoomType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [roomCategoryNames, setRoomCategoryNames] = useState({});
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");

  const fetchRoomCategory = async (RoomCategoryId) => {
    try {
      const response = await getRoomClassByResortIdResorCategoryId(id, RoomCategoryId);
      return response.data.name;
    } catch (error) {
      console.log("Error fetching room class");
      return "";
    }
  };

  const fetchRoomType = async () => {
    try {
      const response = await getRoomTypeByResortId(id);
      const roomType = response.data;
      const roomCategoryNamesPromises = roomType.map(async (room) => {
        const roomCategoryName = await fetchRoomCategory(room.room_category_id);
        return { id: room.id, roomCategoryName };
      });

      const roomCategoryNamesResults = await Promise.all(roomCategoryNamesPromises);
      const roomCategoryNamesMap = roomCategoryNamesResults.reduce(
        (acc, { id, roomCategoryName }) => {
          acc[id] = roomCategoryName;
          return acc;
        },
        {}
      );

      setRoomCategoryNames(roomCategoryNamesMap);
      setRoomType(roomType);
      setLoading(false);
    } catch (error) {
      console.log("Error fetching room category");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoomType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    await fetchRoomType();
  };

  const openEditModal = (roomId, e) => {
    e?.stopPropagation();
    setSelectedTypeId(roomId);
    setIsEditModalOpen(true);
  };

  const closeEditModal = async () => {
    setIsEditModalOpen(false);
    await fetchRoomType();
    setSelectedTypeId(null);
  };

  const handleDelete = async (resort_type_id, e) => {
    e?.stopPropagation();
    if (window.confirm("Are you sure you want to delete this room type?")) {
      try {
        await deleteRoomType(resort_type_id);
        toast.success("Room type deleted successfully");
        await fetchRoomType();
      } catch (error) {
        toast.error("Failed to delete room type");
      }
    }
  };

  const openDetailModal = (roomId) => {
    setSelectedRoomTypeId(roomId);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedRoomTypeId(null);
  };

  const handleImageUploadInDetail = () => {
    fetchRoomType();
  };

  // Filter room types based on search
  const filteredRoomTypes = roomType?.filter((room) =>
    room.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    room.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    room.description?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const roomTypeColors = [
    "from-blue-400 to-indigo-500",
    "from-purple-400 to-pink-500",
    "from-green-400 to-emerald-500",
    "from-orange-400 to-red-500",
    "from-cyan-400 to-blue-500",
    "from-violet-400 to-purple-500",
  ];

  return (
    <div className="room-type-container">
      {/* Header */}
      <div className="room-type-header">
        <div>
          <h2 className="section-title">Room Type Configuration</h2>
          <p className="section-subtitle">Manage different room types and their properties</p>
        </div>
        <button onClick={openModal} className="add-button">
          <PlusIcon className="w-5 h-5" />
          <span>Add Room Type</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-row">
        <div className="stat-card stat-card-blue">
          <div className="stat-icon">
            <HomeIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Total Room Types</p>
            <h3 className="stat-value">{roomType?.length || 0}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-purple">
          <div className="stat-icon">
            <CollectionIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Categories</p>
            <h3 className="stat-value">{new Set(Object.values(roomCategoryNames)).size}</h3>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-wrapper">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Search room types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-actions">
          <div className="results-count">
            <span>{filteredRoomTypes.length} Room Types</span>
          </div>
          <div className="view-toggle">
            <button
              onClick={() => setViewMode("list")}
              className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
            >
              <ViewListIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            >
              <ViewGridIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p className="loading-text">Loading room types...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredRoomTypes.length === 0 && (
        <div className="empty-state">
          <HomeIcon className="empty-icon" />
          <h3 className="empty-title">No Room Types Found</h3>
          <p className="empty-text">
            {searchQuery
              ? "Try adjusting your search criteria"
              : "Get started by creating your first room type"}
          </p>
          {!searchQuery && (
            <button onClick={openModal} className="add-button">
              <PlusIcon className="w-5 h-5" />
              <span>Add Room Type</span>
            </button>
          )}
        </div>
      )}

      {/* List View */}
      {!loading && filteredRoomTypes.length > 0 && viewMode === "list" && (
        <div className="table-container">
          <table className="room-type-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Room Type</th>
                <th>Description</th>
                <th>Category</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoomTypes.map((room, idx) => (
                <tr
                  key={room.id}
                  onClick={() => openDetailModal(room.id)}
                  className="table-row"
                >
                  <td>
                    <span className="room-code">{room.code}</span>
                  </td>
                  <td>
                    <div className="room-cell">
                      <div className={`room-avatar bg-gradient-to-br ${roomTypeColors[idx % roomTypeColors.length]}`}>
                        <HomeIcon className="w-4 h-4" />
                      </div>
                      <span className="room-name">{room.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="room-description">{room.description}</span>
                  </td>
                  <td>
                    <span className="category-badge">{roomCategoryNames[room.id]}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={(e) => openEditModal(room.id, e)}
                        className="action-btn action-btn-edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleDelete(room.id, e)}
                        className="action-btn action-btn-delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Grid View */}
      {!loading && filteredRoomTypes.length > 0 && viewMode === "grid" && (
        <div className="grid-view">
          {filteredRoomTypes.map((room, idx) => (
            <div
              key={room.id}
              onClick={() => openDetailModal(room.id)}
              className="room-card"
            >
              <div className={`room-card-header bg-gradient-to-br ${roomTypeColors[idx % roomTypeColors.length]}`}>
                <HomeIcon className="room-card-icon" />
                <span className="room-card-code">{room.code}</span>
              </div>
              <div className="room-card-body">
                <h3 className="room-card-title">{room.name}</h3>
                <p className="room-card-description">{room.description}</p>
                <div className="room-card-footer">
                  <span className="category-badge">{roomCategoryNames[room.id]}</span>
                </div>
                <div className="room-card-actions">
                  <button
                    onClick={(e) => openEditModal(room.id, e)}
                    className="card-action-btn card-action-btn-edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={(e) => handleDelete(room.id, e)}
                    className="card-action-btn card-action-btn-delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <CreateRoomTypeModal isOpen={isModalOpen} onClose={closeModal} />
      <EditRoomType
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        selectedTypeId={selectedTypeId}
      />
      <RoomTypeDetailModal
        isOpen={isDetailModalOpen}
        onClose={closeDetailModal}
        roomTypeId={selectedRoomTypeId}
        onImageUpload={handleImageUploadInDetail}
      />
      {/* Styles */}
      <style jsx>{`
        .room-type-container {
          padding: 0;
        }

        .room-type-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.25rem 0;
        }

        .section-subtitle {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0;
        }

        .add-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .add-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 3.5rem;
          height: 3.5rem;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: white;
        }

        .stat-card-blue .stat-icon {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }

        .stat-card-purple .stat-icon {
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 0.25rem 0;
        }

        .stat-value {
          font-size: 1.875rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0;
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .search-wrapper {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1.25rem;
          height: 1.25rem;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 3rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .filter-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .results-count {
          padding: 0.5rem 1rem;
          background: #f3f4f6;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .view-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 0.5rem;
          padding: 0.25rem;
        }

        .toggle-btn {
          padding: 0.5rem;
          background: transparent;
          border: none;
          border-radius: 0.375rem;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .toggle-btn.active {
          background: white;
          color: #8b5cf6;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
        }

        .spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #8b5cf6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .loading-text {
          margin-top: 1rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          text-align: center;
        }

        .empty-icon {
          width: 4rem;
          height: 4rem;
          color: #d1d5db;
          margin-bottom: 1rem;
        }

        .empty-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 0.5rem 0;
        }

        .empty-text {
          font-size: 0.875rem;
          color: #9ca3af;
          margin: 0 0 1.5rem 0;
        }

        .table-container {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .room-type-table {
          width: 100%;
          border-collapse: collapse;
        }

        .room-type-table thead {
          background: linear-gradient(135deg, #f9fafb, #ffffff);
          border-bottom: 2px solid #e5e7eb;
        }

        .room-type-table th {
          padding: 1rem 1.5rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .room-type-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.875rem;
        }

        .table-row {
          cursor: pointer;
          transition: background 0.2s;
        }

        .table-row:hover {
          background: #f9fafb;
        }

        .room-code {
          display: inline-block;
          padding: 0.25rem 0.625rem;
          background: #f3e8ff;
          color: #7c3aed;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .room-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .room-avatar {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .room-name {
          font-weight: 600;
          color: #1f2937;
        }

        .room-description {
          color: #6b7280;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .category-badge {
          display: inline-block;
          padding: 0.25rem 0.625rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .action-btn-edit:hover {
          background: #dbeafe;
          transform: scale(1.1);
        }

        .action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: #fee2e2;
          transform: scale(1.1);
        }

        .grid-view {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .room-card {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .room-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .room-card-header {
          height: 120px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          color: white;
          position: relative;
        }

        .room-card-header::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.1);
        }

        .room-card-icon {
          width: 3rem;
          height: 3rem;
          position: relative;
          z-index: 1;
        }

        .room-card-code {
          position: relative;
          z-index: 1;
          font-size: 0.875rem;
          font-weight: 600;
          padding: 0.25rem 0.75rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 9999px;
        }

        .room-card-body {
          padding: 1.5rem;
        }

        .room-card-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .room-card-description {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 1rem 0;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .room-card-footer {
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .room-card-actions {
          display: flex;
          gap: 0.5rem;
        }

        .card-action-btn {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          border: none;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .card-action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .card-action-btn-edit:hover {
          background: #dbeafe;
        }

        .card-action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .card-action-btn-delete:hover {
          background: #fee2e2;
        }

        @media (max-width: 1024px) {
          .room-type-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .add-button {
            width: 100%;
            justify-content: center;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-wrapper {
            max-width: 100%;
          }

          .filter-actions {
            justify-content: space-between;
          }
        }

        @media (max-width: 768px) {
          .stats-row {
            grid-template-columns: 1fr;
          }

          .grid-view {
            grid-template-columns: 1fr;
          }

          .room-description {
            max-width: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default RoomType;
